import React, {useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Box, Button, CircularProgress, TextField, Typography} from '@mui/material';
import {DeleteForever, WarningAmber} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import Swal from 'sweetalert2';
import {announceLogout} from '../utilities/WebSocketProvider';
import {clearSageHistory} from '../utilities/Utils';

const DARK = 'var(--lm-bg-1a1f2b)';
const DANGER = '#e53935';
const BORDER = 'rgba(229, 57, 53, 0.45)';

// Mirrors Billing.js: a subscription in one of these states is still being charged.
const ENTITLING_STATUSES = ['active', 'trialing', 'past_due'];

const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        color: 'var(--lm-tx-f5f5f5)',
        '& fieldset': {borderColor: 'rgba(var(--lm-bd-f5f5f5-rgb),0.25)'},
        '&:hover fieldset': {borderColor: 'rgba(var(--lm-bd-f5f5f5-rgb),0.45)'},
        '&.Mui-focused fieldset': {borderColor: DANGER},
    },
    '& .MuiInputLabel-root': {color: 'var(--lm-tx-9e9e9e)'},
    '& .MuiInputLabel-root.Mui-focused': {color: DANGER},
};

// Self-serve account deletion (DELETE /api/account). The backend sets any renewing Stripe
// subscription to cancel at period end, locks the account at once, emails a cancellation link,
// and purges it after the grace period; see AccountDeletionService. The subscription check
// here only decides which notice to show.
export default function DeleteAccountDialog({show, onHide}) {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    // 'checking' | 'none' | 'renewing' | 'ending' | 'unknown'
    const [subscription, setSubscription] = useState('checking');

    useEffect(() => {
        if (!show) return;
        setPassword('');
        setError('');
        setSubscription('checking');
        const user = JSON.parse(sessionStorage.getItem('user'));
        fetch(`${process.env.REACT_APP_API_PROXY}/billing/subscription`, {
            headers: {'Authorization': 'Bearer ' + user?.accessToken},
        })
            .then(async res => {
                if (!res.ok) throw new Error('Request failed with status ' + res.status);
                const text = await res.text();
                const sub = text ? JSON.parse(text) : null;
                if (!sub || !ENTITLING_STATUSES.includes(sub.status)) setSubscription('none');
                else setSubscription(sub.cancelAtPeriodEnd ? 'ending' : 'renewing');
            })
            .catch(() => setSubscription('unknown'));
    }, [show]);

    function logOut() {
        announceLogout();
        sessionStorage.removeItem('user');
        clearSageHistory();
        navigate('/');
    }

    async function handleDelete(e) {
        e.preventDefault();
        if (!password || busy) return;
        setBusy(true);
        setError('');
        const user = JSON.parse(sessionStorage.getItem('user'));
        try {
            const res = await fetch(`${process.env.REACT_APP_API_PROXY}/account`, {
                method: 'DELETE',
                headers: {'Authorization': 'Bearer ' + user?.accessToken, 'Content-Type': 'application/json'},
                body: JSON.stringify({password}),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || data.errors?.[0] || "Couldn't delete your account. Please try again.");
                return;
            }
            onHide();
            logOut();
            Swal.fire({
                icon: 'info',
                title: 'Account scheduled for deletion',
                text: 'Your account is locked and will be deleted in 30 days. We sent a link to your email address that cancels the deletion if you change your mind.',
                confirmButtonColor: 'var(--th-6c4f9b)',
            });
        } catch {
            setError("Couldn't reach the server. Check your connection and try again.");
        } finally {
            setBusy(false);
        }
    }

    const canSubmit = !!password && !busy && subscription !== 'checking';

    return (
        <Modal show={show} onHide={busy ? undefined : onHide} centered data-bs-theme="dark">
            <Modal.Header closeButton={!busy} style={{
                backgroundColor: DARK, borderBottom: `1px solid ${BORDER}`,
                background: `radial-gradient(ellipse at 50% 0%, rgba(229, 57, 53, 0.25) 0%, ${DARK} 80%)`,
                alignItems: 'center', paddingTop: 12, paddingBottom: 12,
            }}>
                <DeleteForever style={{fontSize: 26, color: DANGER, marginRight: 10}}/>
                <Modal.Title style={{color: 'var(--lm-tx-f5f5f5)', fontWeight: 700, fontSize: '1.05rem'}}>
                    Delete account
                </Modal.Title>
            </Modal.Header>

            <form onSubmit={handleDelete}>
                <Modal.Body style={{backgroundColor: DARK, color: 'var(--lm-tx-f5f5f5)', padding: '20px 24px'}}>
                    <Typography sx={{fontSize: '0.9rem', mb: 1}}>What happens when you delete your account:</Typography>
                    <Box component="ul" sx={{fontSize: '0.85rem', color: '#C8C8D4', pl: 2.5, mb: 2, '& li': {mb: 0.5}}}>
                        <li>You're signed out and your account is locked straight away.</li>
                        <li>We email you a link that restores it. It works for 30 days.</li>
                        <li>After 30 days we permanently delete your profile, email address, password, uploaded files and sign-in history. This can't be undone.</li>
                        <li>Content you added to campaigns run by other people may stay visible to those campaigns.</li>
                    </Box>

                    {subscription === 'checking' && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'var(--lm-tx-9e9e9e)', fontSize: '0.85rem'}}>
                            <CircularProgress size={14} sx={{color: 'var(--lm-tx-9e9e9e)'}}/> Checking your subscription…
                        </Box>
                    )}
                    {subscription === 'renewing' && (
                        <Notice>
                            Your paid plan will be cancelled and you won't be charged again. You lose access to it now, and the
                            rest of the current billing period isn't refunded. If you restore your account within 30 days, the
                            plan renews as normal.
                        </Notice>
                    )}
                    {subscription === 'ending' && (
                        <Notice>
                            Your paid plan is already cancelled. Deleting your account ends your access to it now rather than at
                            the end of the billing period, and the remaining time isn't refunded.
                        </Notice>
                    )}

                    <TextField
                        id="delete-account-password"
                        type="password"
                        label="Enter your password to confirm"
                        autoComplete="current-password"
                        fullWidth
                        size="small"
                        value={password}
                        disabled={busy}
                        onChange={e => { setPassword(e.target.value); setError(''); }}
                        error={!!error}
                        helperText={error || ' '}
                        sx={FIELD_SX}
                    />
                </Modal.Body>

                <Modal.Footer style={{backgroundColor: DARK, borderTop: `1px solid ${BORDER}`, padding: '12px 24px'}}>
                    <Button onClick={onHide} disabled={busy} sx={{color: 'var(--lm-tx-9e9e9e)', '&:hover': {color: 'var(--lm-tx-f5f5f5)'}}}>
                        Keep my account
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!canSubmit}
                        startIcon={busy ? <CircularProgress size={14} sx={{color: 'var(--lm-tx-f5f5f5)'}}/> : <DeleteForever/>}
                        sx={{
                            ml: 1, backgroundColor: DANGER, '&:hover': {backgroundColor: '#c62828'},
                            '&:disabled': {backgroundColor: 'rgba(229, 57, 53, 0.3)', color: 'rgba(245,245,245,0.4)'},
                        }}
                    >
                        {busy ? 'Deleting…' : 'Delete my account'}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

function Notice({children}) {
    return (
        <Box sx={{
            display: 'flex', gap: 1, mb: 2, p: 1.25, borderRadius: 1, fontSize: '0.85rem', lineHeight: 1.5,
            backgroundColor: 'rgba(229, 57, 53, 0.12)', border: `1px solid ${BORDER}`,
        }}>
            <WarningAmber sx={{fontSize: 18, color: '#ffb74d', flexShrink: 0, mt: '2px'}}/>
            <Box>{children}</Box>
        </Box>
    );
}
